import { LevelCatalog, Modes } from "@highmarch/app";
import { ChestBand, ChestResult, GearItem, LevelModifiers } from "@highmarch/domain";
import { systemClock } from "../infra/adapters";
import { parkUnlockCards, unlockCards } from "../ui/unlockCards";

/** What a finished run banks into the save, given by the level flow (boot/levelFlow.ts). */
export interface RunBankDeps {
  /** Banks the level's chest (and parks the unlock cards it opens); the gear it held, empty without a chest. */
  bankChest(): GearItem[];
  /** Records the run's result in the catalog (a win clears the level and unlocks the next). */
  recordResult(won: boolean): void;
  /** Writes the catalog into the save and stores it. */
  persist(): void;
}

/**
 * A run's result and chest, banked exactly once each and as soon as the run is over (M33).
 *
 * Both used to be banked only when the player left the end screen (or tapped OPEN): the end screen's 3D chest — a
 * second WebGL context beside the level's — is the most memory the game ever holds at once, and when iOS closed the
 * page there, the win and the chest went with it (the battle's suspended copy had already been dropped as the run
 * ended). Now `settle` runs before the end screen is drawn; OPEN only reveals what is already in the save (`takeGear`)
 * and leaving settles again, which changes nothing.
 */
export class RunBank {
  private chestBanked = false;
  private resultRecorded = false;
  private gear: GearItem[] = [];
  private gearTaken = false;

  constructor(private readonly d: RunBankDeps) {}

  /** Whether the run's result is in the save. */
  get settled(): boolean { return this.resultRecorded; }

  /** The run is over (won or lost): the chest and the result go into the save now, once. */
  settle(won: boolean): void {
    const changed = !this.chestBanked || !this.resultRecorded;
    this.bankChestOnce();
    if (!this.resultRecorded) { this.resultRecorded = true; this.d.recordResult(won); }
    if (changed) this.d.persist();
  }

  /** OPEN on the end screen: the gear the chest held, for the reveal (once; banked first if it was not yet). */
  takeGear(): GearItem[] {
    if (!this.chestBanked) { this.bankChestOnce(); this.d.persist(); }
    if (this.gearTaken) return [];
    this.gearTaken = true;
    return this.gear;
  }

  private bankChestOnce(): void {
    if (this.chestBanked) return;
    this.chestBanked = true;
    this.gear = this.d.bankChest();
  }
}

/**
 * A level's bank (moved from boot/levelFlow.ts): its chest and its result. An Endless, Daily Road or challenge-code run is
 * not a result of the level whose ground it borrows (M21, MP-20, MP-28); a Daily Road or challenge run has no chest either.
 */
export function levelRunBank(catalog: LevelCatalog, level: number, modifiers: LevelModifiers, chest: () => ChestResult | null, persist: () => void): RunBank {
  const kind = new Modes(catalog).runKind(modifiers), borrowed = kind !== null && kind !== "iron";   // Endless, Daily Road, challenge codes and the event modes (MP-21) borrow a layout
  return new RunBank({
    bankChest: () => {
      const c = chest();
      if (!c) return [];
      // What this win opens (Gear, Chests, a new tower) plays as cards after COLLECT, or on the hub if never collected.
      const before = catalog.highestLevelCleared;
      parkUnlockCards(c.band === ChestBand.Loss ? [] : unlockCards(catalog.balance, before, Math.max(before, c.level)));
      return catalog.bankChest(c, true);                      // the level's own chest: VIP resource production applies
    },
    recordResult: (won) => { if (!borrowed) catalog.recordResult(level, won, systemClock.utcNowSeconds()); },
    persist,
  });
}
